import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './lib/cn';

const buttonVariants = cva(
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-[14px] border border-transparent px-5 text-sm font-extrabold tracking-[-0.015em] transition-[transform,background-color,color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)] disabled:pointer-events-none disabled:opacity-45 aria-disabled:pointer-events-none aria-disabled:opacity-45 active:translate-y-px',
  {
    variants: {
      variant: {
        primary:
          'border-[color-mix(in_srgb,var(--brand)_88%,transparent)] bg-[var(--brand)] text-[var(--brand-foreground)] shadow-[var(--button-primary-shadow)] hover:-translate-y-0.5 hover:bg-[var(--brand-strong)] hover:shadow-[var(--button-primary-shadow-hover)] button-shimmer',
        secondary:
          'border-[var(--border)] bg-[var(--surface)] text-[var(--foreground)] shadow-[var(--button-secondary-shadow)] hover:-translate-y-0.5 hover:border-[var(--border-strong)] hover:bg-[var(--surface-raised)] hover:shadow-[var(--button-secondary-shadow-hover)]',
        ghost:
          'text-[var(--muted-foreground)] hover:bg-[var(--surface-raised)] hover:text-[var(--foreground)]',
        danger:
          'border-[color-mix(in_srgb,var(--danger)_88%,transparent)] bg-[var(--danger)] text-[var(--brand-foreground)] shadow-[var(--button-secondary-shadow)] hover:-translate-y-0.5 hover:brightness-95',
      },
      size: {
        sm: 'min-h-10 rounded-[12px] px-4 text-xs',
        md: 'min-h-11 px-5',
        lg: 'min-h-13 rounded-[16px] px-7 text-base',
        icon: 'size-11 shrink-0 rounded-[13px] p-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  children?: ReactNode;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  loadingLabel = 'Carregando',
  disabled,
  children,
  ...props
}: ButtonProps) {
  if (asChild) {
    return (
      <Slot
        className={cn(buttonVariants({ variant, size }), className)}
        aria-busy={loading || undefined}
        aria-disabled={disabled || loading || undefined}
        data-loading={loading || undefined}
        {...props}
      >
        {children}
      </Slot>
    );
  }

  return (
    <button
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-loading={loading || undefined}
      {...props}
    >
      {loading && (
        <LoaderCircle
          className="size-4 animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
      )}
      {children}
      {loading && <span className="sr-only">{loadingLabel}</span>}
    </button>
  );
}
